import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { cloneDeep } from 'lodash';
import { calcClientDerivatives } from '@shared';
import { selectClient } from '@client/model/selectors';
import { Quote } from '@client/model/quotesModel';
import { calculateTotalPolicyPremiumCost } from '@client/util/fundingSufficiency';
import { isPolicyAddedViaShopInsurance } from '@client/util/policyFundingSourceUtils';
import {
  calculatePremiumDeductions,
  type PremiumDeduction,
} from '@client/util/ltcFinancialPlanning';
import { useLtcPlanningCalculations } from './useLtcPlanningCalculations';
import { useLtcPlanningQuotes } from './useLtcPlanningQuotes';
import {
  createPolicyFundingSourceFromQuote,
  createPolicyFundingSourceFromTraditionalLTCQuote,
} from '@client/util/policyQuoteCalculations';

interface ScenarioMetrics {
  ltcCoverage: number;
  roi: number;
  assetsRemaining: number;
  coveragePercent: number;
  nonLtcPayout: number;
  coverageGap: number;
}

interface ComparisonResult {
  projectedLtcCost: number;
  withoutInsurance: ScenarioMetrics;
  withInsurance: ScenarioMetrics;
  hasPolicy: boolean;
  netBenefit: number;
  insuranceIsBetter: boolean;
  benchmarkPolicyName: string | null;
}

/**
 * Hook that generates two client scenarios (with/without shop insurance policies),
 * runs them through calcClientDerivatives, and compares LTC coverage, ROI, and remaining assets.
 * If no policies have been added, uses the best ROI quote as a comparison benchmark.
 */
export function useLtcComparisonScenarios(): ComparisonResult {
  const client = useSelector(selectClient);
  const { projectedLtcCost } = useLtcPlanningCalculations();
  const { allQuotes } = useLtcPlanningQuotes({ enabled: true });

  // Check if any policies have been added via shop insurance
  const hasPolicy = useMemo(() => {
    const ltcPolicies = client?.multipleFundingSources?.ltcPolicy || {};
    return Object.values(ltcPolicies).some(policy => isPolicyAddedViaShopInsurance(policy));
  }, [client]);

  // Get the best ROI quote (quotes are already sorted by ROI descending)
  const bestQuote = useMemo(() => {
    return allQuotes.length > 0 ? allQuotes[0] : null;
  }, [allQuotes]);

  // Scenario 1: Without Shop Insurance
  const withoutInsuranceMetrics = useMemo(() => {
    if (!client) {
      return {
        ltcCoverage: 0,
        roi: 0,
        assetsRemaining: 0,
        coveragePercent: 0,
        nonLtcPayout: 0,
        coverageGap: 0,
      };
    }

    // Clone the client
    const clientClone = cloneDeep(client);

    // Filter out policies added via shop insurance
    const ltcPolicies = clientClone.multipleFundingSources?.ltcPolicy || {};
    const filteredPolicies: Record<string, PolicyFundingSource> = {};

    Object.entries(ltcPolicies).forEach(([fundingSourceId, policy]) => {
      if (!isPolicyAddedViaShopInsurance(policy)) {
        filteredPolicies[fundingSourceId] = policy;
      }
    });

    // Update the client with filtered policies
    if (clientClone.multipleFundingSources) {
      clientClone.multipleFundingSources.ltcPolicy = filteredPolicies;
    }

    // Run through calcClientDerivatives
    const calculatedClient = calcClientDerivatives(clientClone, null);

    // Extract metrics
    const combinedProjectedCostCoverage =
      calculatedClient.fundingSources?.combinedProjectedCostCoverage || 0;
    const multipleSelfFundingProjectedCostCoverage =
      calculatedClient.multipleFundingSources?.multipleSelfFundingProjectedCostCoverage || 0;
    const roi = calculatedClient.fundingSources?.combinedProjectedReturnOnInvestment || 0;
    const nonLtcPayout = calculatedClient.fundingSources?.combinedNonLtcPayout || 0;

    // Calculate assets remaining: max(0, combinedProjectedCostCoverage - projectedCost)
    const assetsRemaining = Math.max(0, combinedProjectedCostCoverage - projectedLtcCost);

    // Calculate coverage gap
    const coverageGap = Math.max(0, projectedLtcCost - combinedProjectedCostCoverage);

    const coveragePercent =
      projectedLtcCost > 0
        ? Math.round((combinedProjectedCostCoverage / projectedLtcCost) * 100)
        : 0;

    return {
      ltcCoverage: combinedProjectedCostCoverage,
      roi,
      assetsRemaining,
      coveragePercent,
      nonLtcPayout,
      coverageGap,
    };
  }, [client, projectedLtcCost]);

  // Scenario 2: With Shop Insurance (or best quote if no policy added)
  const withInsuranceMetrics = useMemo(() => {
    if (!client) {
      return {
        ltcCoverage: 0,
        roi: 0,
        assetsRemaining: 0,
        coveragePercent: 0,
        nonLtcPayout: 0,
        coverageGap: 0,
      };
    }

    // Clone the client
    const clientClone = cloneDeep(client);

    // If no policies have been added, simulate adding the best quote
    if (!hasPolicy && bestQuote) {
      // Initialize multipleFundingSources if needed
      if (!clientClone.multipleFundingSources) {
        clientClone.multipleFundingSources = {
          selfFunding: {},
          ltcPolicy: {},
          annuity: {},
        };
      }

      // Create a policy funding source from the best quote
      const isTraditionalLTC = !!(bestQuote as any).traditionalLTCQuoteId;
      const policyFundingSource = isTraditionalLTC
        ? createPolicyFundingSourceFromTraditionalLTCQuote(
            bestQuote as unknown as TraditionalLTCQuote,
            client,
          )
        : createPolicyFundingSourceFromQuote(bestQuote as unknown as Quote, client);

      // Add the simulated policy with a temporary ID
      const tempPolicyId = 'temp-benchmark-policy';
      clientClone.multipleFundingSources.ltcPolicy = {
        ...clientClone.multipleFundingSources.ltcPolicy,
        [tempPolicyId]: {
          ...policyFundingSource,
          addedViaShopInsurance: true,
        },
      };
    }

    // Calculate total policy cost for shop insurance policies
    const totalPolicyCost = calculateTotalPolicyPremiumCost(clientClone.multipleFundingSources);

    // Calculate and apply premium deductions using shared utility
    const deductions = calculatePremiumDeductions(
      clientClone.multipleFundingSources,
      totalPolicyCost,
    );

    // Apply deductions to clientClone
    deductions.forEach((deduction: PremiumDeduction) => {
      const { fundingSourceId, newValue } = deduction;
      if (clientClone.multipleFundingSources?.selfFunding?.[fundingSourceId]) {
        clientClone.multipleFundingSources.selfFunding[fundingSourceId].existingAssetsValue =
          newValue;
      }
    });

    // Run through calcClientDerivatives
    const calculatedClient = calcClientDerivatives(clientClone, null);

    // Extract metrics
    const combinedProjectedCostCoverage =
      calculatedClient.fundingSources?.combinedProjectedCostCoverage || 0;
    const multipleSelfFundingProjectedCostCoverage =
      calculatedClient.multipleFundingSources?.multipleSelfFundingProjectedCostCoverage || 0;
    const roi = calculatedClient.fundingSources?.combinedProjectedReturnOnInvestment || 0;
    const nonLtcPayout = calculatedClient.fundingSources?.combinedNonLtcPayout || 0;

    // Calculate assets remaining: max(0, combinedProjectedCostCoverage - projectedCost)
    const assetsRemaining = Math.max(0, combinedProjectedCostCoverage - projectedLtcCost);

    // Calculate coverage gap
    const coverageGap = Math.max(0, projectedLtcCost - combinedProjectedCostCoverage);

    const coveragePercent =
      projectedLtcCost > 0
        ? Math.round((combinedProjectedCostCoverage / projectedLtcCost) * 100)
        : 0;

    return {
      ltcCoverage: combinedProjectedCostCoverage,
      roi,
      assetsRemaining,
      coveragePercent,
      nonLtcPayout,
      coverageGap,
    };
  }, [client, projectedLtcCost, hasPolicy, bestQuote]);

  // Calculate net benefit
  const netBenefit = Math.abs(
    withInsuranceMetrics.ltcCoverage - withoutInsuranceMetrics.ltcCoverage,
  );
  const insuranceIsBetter = withInsuranceMetrics.ltcCoverage > withoutInsuranceMetrics.ltcCoverage;

  // Get benchmark policy name when using best quote
  const benchmarkPolicyName =
    !hasPolicy && bestQuote ? (bestQuote as any).productName || null : null;

  return {
    projectedLtcCost,
    withoutInsurance: withoutInsuranceMetrics,
    withInsurance: withInsuranceMetrics,
    hasPolicy,
    netBenefit,
    insuranceIsBetter,
    benchmarkPolicyName,
  };
}
